import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, TableShell } from "@/components/EntityManager";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertTriangle, FileWarning, PackageCheck, Truck, IndianRupee, FileX2, MessageCircle, FilePlus2, Printer, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { printOutstanding45 } from "@/lib/print";
import { fmtDate } from "@/lib/format";
import { inr } from "@/lib/format";
import { toast } from "sonner";

const chaseWhatsApp = (b) => {
  if (!b.customer_phone) {
    toast.error(`No phone saved for ${b.customer_name} in Customer Master.`);
    return false;
  }
  const digits = String(b.customer_phone).replace(/\D/g, "");
  const full = digits.length === 10 ? "91" + digits : digits;
  const text = [
    `Dear ${b.customer_name},`,
    `Gentle payment reminder from Nisha Transport Corporation.`,
    `Bill No. ${String(b.invoice_no || "").replace(/^INV[-\s]?/i, "")} dated ${b.issue_date ? fmtDate(b.issue_date) : "—"} is outstanding for ${b.days} days.`,
    `Bill Amount: ${inr(b.total || 0)}${(b.paid || 0) > 0 ? ` · Received: ${inr(b.paid)}` : ""}`,
    `Balance Due: ${inr(b.outstanding || 0)}`,
    `Kindly arrange payment at the earliest. Thank you.`,
    `— Nisha Transport Corporation`,
  ].join("\n");
  window.open(`https://wa.me/${full}?text=${encodeURIComponent(text)}`, "_blank");
  return true;
};

const agoText = (iso) => {
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return d <= 0 ? "today" : `${d}d ago`;
};

const chaseDue = (chased, inv) => {
  const t = chased[String(inv || "")];
  if (!t) return true;
  return (Date.now() - new Date(t).getTime()) / 86400000 > 7;
};

export default function Reminders() {
  const navigate = useNavigate();
  const [docs, setDocs] = useState(null);
  const [pod, setPod] = useState(null);
  const [eway, setEway] = useState(null);
  const [out45, setOut45] = useState(null);
  const [unbilled, setUnbilled] = useState(null);
  const [chased, setChased] = useState({});

  const chase = (b) => {
    if (!chaseWhatsApp(b)) return;
    api.post("/reminder-log", { customer_name: b.customer_name, bills: [String(b.invoice_no || "")], total_balance: b.outstanding || 0 })
      .then((r) => setChased((p) => ({ ...p, [String(b.invoice_no || "")]: r.data.sent_at })))
      .catch(() => {});
  };

  useEffect(() => {
    api.get("/reports/expiring-docs").then((r) => setDocs(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    api.get("/reports/pod-pending").then((r) => setPod(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    api.get("/reports/expiring-eway").then((r) => setEway(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    api.get("/reminder-log/by-bill").then((r) => setChased(r.data || {})).catch(() => {});
    api.get("/reports/outstanding").then((r) => {
      const items = (r.data?.rows || []).filter((x) => (x.outstanding || 0) > 0 && (x.days || 0) > 45);
      setOut45({ items, count: items.length, total: items.reduce((a, x) => a + (x.outstanding || 0), 0) });
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    api.get("/reports/unbilled").then((r) => {
      const items = (r.data?.shipments || []).slice().sort((a, b) => String(a.lr_date || a.dispatch_date || "").localeCompare(String(b.lr_date || b.dispatch_date || "")));
      setUnbilled({ items, count: r.data?.count || items.length, total: r.data?.total_unbilled || 0 });
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);

  return (
    <div>
      <PageHeader title="Reminders" subtitle="Document expiry, e-way bills, 45+ day outstanding, unbilled consignments and pending POD uploads." />

      <div className="mb-8">
        <h3 className="mb-3 flex items-center gap-2 font-display text-lg font-semibold">
          <FileWarning className="h-5 w-5 text-amber-500" /> Document Expiry (next 30 days)
          {docs && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">{docs.count}</span>}
        </h3>
        <TableShell>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                {["Driver", "Vehicle No.", "Document", "Expiry Date", "Status"].map((h) => (
                  <TableHead key={h} className="text-[11px] uppercase tracking-[0.15em] text-slate-500">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {docs === null &&
                [1, 2].map((i) => (
                  <TableRow key={i}>{Array.from({ length: 5 }).map((_, j) => <TableCell key={j}><Skeleton className="h-4 w-20" /></TableCell>)}</TableRow>
                ))}
              {docs && docs.items.length === 0 && (
                <TableRow><TableCell colSpan={5} className="py-10 text-center text-muted-foreground">No documents expiring soon. 👍</TableCell></TableRow>
              )}
              {docs && docs.items.map((d, i) => (
                <TableRow key={i} data-testid="expiry-row">
                  <TableCell className="text-sm font-medium">{d.driver_name || "—"}</TableCell>
                  <TableCell className="text-sm">{d.vehicle_no || "—"}</TableCell>
                  <TableCell className="text-sm">{d.doc_type}</TableCell>
                  <TableCell className="text-sm">{fmtDate(d.expiry)}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${d.expired ? "border-rose-200 bg-rose-50 text-rose-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>
                      <AlertTriangle className="h-3 w-3" />
                      {d.expired ? `Expired ${Math.abs(d.days_left)}d ago` : `${d.days_left}d left`}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableShell>
      </div>

      <div className="mb-8">
        <h3 className="mb-3 flex items-center gap-2 font-display text-lg font-semibold">
          <IndianRupee className="h-5 w-5 text-rose-600" /> Outstanding above 45 days
          {out45 && <span className="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-700" data-testid="out45-count">{out45.count}{out45.total > 0 ? ` · ${inr(out45.total)}` : ""}</span>}
          {out45 && out45.items.length > 0 && (
            <span className="ml-auto flex gap-2">
              <Button size="sm" variant="outline" className="h-8" onClick={() => printOutstanding45(out45.items)} data-testid="out45-print-btn">
                <Printer className="mr-1.5 h-3.5 w-3.5" /> Print
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-8"
                onClick={() => {
                  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
                  const lines = ["Sl,Branch,Bill No.,Bill Date,Customer,Bill Amount,Received,Balance,Age (days)"];
                  out45.items.forEach((b, i) =>
                    lines.push([i + 1, esc(b.office), esc(String(b.invoice_no || "").replace(/^INV[-\s]?/i, "")), esc(b.issue_date ? fmtDate(b.issue_date) : ""), esc(b.customer_name), b.total || 0, b.paid || 0, b.outstanding || 0, b.days].join(","))
                  );
                  const a = document.createElement("a");
                  a.href = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
                  a.download = "outstanding-45-days.csv";
                  a.click();
                }}
                data-testid="out45-excel-btn"
              >
                <Download className="mr-1.5 h-3.5 w-3.5" /> Excel
              </Button>
            </span>
          )}
        </h3>
        <TableShell>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                {["Branch", "Bill No.", "Bill Date", "Customer", "Bill Amount", "Received", "Balance", "Age", "Chase"].map((h) => (
                  <TableHead key={h} className="text-[11px] uppercase tracking-[0.15em] text-slate-500">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {out45 === null &&
                [1, 2].map((i) => (
                  <TableRow key={i}>{Array.from({ length: 9 }).map((_, j) => <TableCell key={j}><Skeleton className="h-4 w-20" /></TableCell>)}</TableRow>
                ))}
              {out45 && out45.items.length === 0 && (
                <TableRow><TableCell colSpan={9} className="py-10 text-center text-muted-foreground">No bills outstanding beyond 45 days. 🎉</TableCell></TableRow>
              )}
              {out45 && out45.items.map((b, i) => (
                <TableRow key={i} className={chaseDue(chased, b.invoice_no) ? "bg-amber-50/70 hover:bg-amber-50" : ""} data-testid="out45-row">
                  <TableCell className="text-sm">{b.office || "—"}</TableCell>
                  <TableCell className="text-sm font-medium">{String(b.invoice_no || "—").replace(/^INV[-\s]?/i, "")}</TableCell>
                  <TableCell className="text-sm">{b.issue_date ? fmtDate(b.issue_date) : "—"}</TableCell>
                  <TableCell className="text-sm">{b.customer_name || "—"}</TableCell>
                  <TableCell className="text-sm">{inr(b.total || 0)}</TableCell>
                  <TableCell className="text-sm">{inr(b.paid || 0)}</TableCell>
                  <TableCell className="text-sm font-semibold text-rose-700">{inr(b.outstanding || 0)}</TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2.5 py-0.5 text-xs font-medium text-rose-700">
                      <AlertTriangle className="h-3 w-3" /> {b.days}d
                    </span>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                      onClick={() => chase(b)}
                      data-testid="out45-whatsapp-btn"
                    >
                      <MessageCircle className="mr-1.5 h-3.5 w-3.5" /> WhatsApp
                    </Button>
                    {chased[String(b.invoice_no || "")] ? (
                      <div className="mt-1 text-[11px] font-medium text-slate-500" data-testid="out45-last-chased">
                        Reminded {agoText(chased[String(b.invoice_no || "")])}
                        {chaseDue(chased, b.invoice_no) && <span className="ml-1 font-bold text-amber-700" data-testid="out45-chase-due">· Chase again</span>}
                      </div>
                    ) : (
                      <div className="mt-1 text-[11px] font-bold text-amber-700" data-testid="out45-chase-due">Never chased — follow up</div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableShell>
      </div>

      <div className="mb-8">
        <h3 className="mb-3 flex items-center gap-2 font-display text-lg font-semibold">
          <FileX2 className="h-5 w-5 text-amber-600" /> Unbilled Consignments
          {unbilled && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700" data-testid="unbilled-count">{unbilled.count}{unbilled.total > 0 ? ` · ${inr(unbilled.total)}` : ""}</span>}
        </h3>
        <TableShell>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                {["LR No.", "LR Date", "From", "Destination", "Customer", "Branch", "Freight", "Action"].map((h) => (
                  <TableHead key={h} className="text-[11px] uppercase tracking-[0.15em] text-slate-500">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {unbilled === null &&
                [1, 2].map((i) => (
                  <TableRow key={i}>{Array.from({ length: 8 }).map((_, j) => <TableCell key={j}><Skeleton className="h-4 w-20" /></TableCell>)}</TableRow>
                ))}
              {unbilled && unbilled.items.length === 0 && (
                <TableRow><TableCell colSpan={8} className="py-10 text-center text-muted-foreground">Every consignment is billed. 🎉</TableCell></TableRow>
              )}
              {unbilled && unbilled.items.map((s) => (
                <TableRow key={s.id} data-testid="unbilled-row">
                  <TableCell className="text-sm font-medium">{s.lr_no || s.consignment_no || "—"}</TableCell>
                  <TableCell className="text-sm">{(s.lr_date || s.dispatch_date) ? fmtDate(s.lr_date || s.dispatch_date) : "—"}</TableCell>
                  <TableCell className="text-sm">{s.origin || "—"}</TableCell>
                  <TableCell className="text-sm">{s.destination || "—"}</TableCell>
                  <TableCell className="text-sm">{s.customer_name || "—"}</TableCell>
                  <TableCell className="text-sm">{s.office || "—"}</TableCell>
                  <TableCell className="text-sm font-semibold">{inr(s.freight_amount || 0)}</TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 border-blue-200 text-blue-700 hover:bg-blue-50"
                      onClick={() => navigate(`/bill-create?shipment=${s.id}`)}
                      data-testid="unbilled-create-bill-btn"
                    >
                      <FilePlus2 className="mr-1.5 h-3.5 w-3.5" /> Create Bill
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableShell>
      </div>

      <div className="mb-8">
        <h3 className="mb-3 flex items-center gap-2 font-display text-lg font-semibold">
          <Truck className="h-5 w-5 text-rose-500" /> E-Way Bill Expiry (undelivered, expiring within 1 day)
          {eway && <span className="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-700">{eway.count}</span>}
        </h3>
        <TableShell>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                {["E-Way Bill No.", "LR No.", "From", "Destination", "Valid Upto", "Status"].map((h) => (
                  <TableHead key={h} className="text-[11px] uppercase tracking-[0.15em] text-slate-500">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {eway === null &&
                [1, 2].map((i) => (
                  <TableRow key={i}>{Array.from({ length: 6 }).map((_, j) => <TableCell key={j}><Skeleton className="h-4 w-20" /></TableCell>)}</TableRow>
                ))}
              {eway && eway.items.length === 0 && (
                <TableRow><TableCell colSpan={6} className="py-10 text-center text-muted-foreground">No e-way bills expiring soon. 👍</TableCell></TableRow>
              )}
              {eway && eway.items.map((e, i) => (
                <TableRow key={i} data-testid="eway-expiry-row">
                  <TableCell className="text-sm font-medium">{e.eway_bill || "—"}</TableCell>
                  <TableCell className="text-sm">{e.lr_no || "—"}</TableCell>
                  <TableCell className="text-sm">{e.origin || "—"}</TableCell>
                  <TableCell className="text-sm">{e.destination || "—"}</TableCell>
                  <TableCell className="text-sm">{fmtDate(e.eway_valid)}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${e.expired ? "border-rose-200 bg-rose-50 text-rose-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>
                      <AlertTriangle className="h-3 w-3" />
                      {e.expired ? `Expired ${Math.abs(e.days_left)}d ago` : e.days_left === 0 ? "Expires today" : `${e.days_left}d left`}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableShell>
      </div>

      <div>
        <h3 className="mb-3 flex items-center gap-2 font-display text-lg font-semibold">
          <PackageCheck className="h-5 w-5 text-primary" /> POD Pending (delivered, no upload)
          {pod && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">{pod.count}</span>}
        </h3>
        <TableShell>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                {["LR No.", "From", "Destination", "Customer", "Vehicle No.", "Delivery Date"].map((h) => (
                  <TableHead key={h} className="text-[11px] uppercase tracking-[0.15em] text-slate-500">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {pod === null &&
                [1, 2].map((i) => (
                  <TableRow key={i}>{Array.from({ length: 6 }).map((_, j) => <TableCell key={j}><Skeleton className="h-4 w-20" /></TableCell>)}</TableRow>
                ))}
              {pod && pod.items.length === 0 && (
                <TableRow><TableCell colSpan={6} className="py-10 text-center text-muted-foreground">All delivered consignments have PODs. 🎉</TableCell></TableRow>
              )}
              {pod && pod.items.map((p) => (
                <TableRow key={p.id} data-testid="pod-pending-row">
                  <TableCell className="text-sm font-medium">{p.lr_no || "—"}</TableCell>
                  <TableCell className="text-sm">{p.origin || "—"}</TableCell>
                  <TableCell className="text-sm">{p.destination || "—"}</TableCell>
                  <TableCell className="text-sm">{p.customer_name || "—"}</TableCell>
                  <TableCell className="text-sm">{p.vehicle_no || "—"}</TableCell>
                  <TableCell className="text-sm">{p.delivery_date ? fmtDate(p.delivery_date) : "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableShell>
      </div>
    </div>
  );
}
